import type { FrontendVariables } from "@Front/Config/VariablesFront";
import { alignMultisite4nkWebLineHostToPageLine } from "le-coffre-resources/dist/browserPublicHostPatterns.js";
import {
	rewriteBackApiHostWhenApiSubdomainMirrorsPage,
	rewriteLegacyLecoffreIoFrontUrlOnV2NotaryPage,
} from "@Front/Config/configRuntimeUrlHelpers";

function applyLegacyBackApiHostRewriteInBrowser(instance: FrontendVariables): boolean {
	if (typeof window === "undefined") {
		return false;
	}
	const nextHost = rewriteBackApiHostWhenApiSubdomainMirrorsPage(window.location.host, instance.BACK_API_HOST);
	if (nextHost === instance.BACK_API_HOST) {
		return false;
	}
	instance.BACK_API_HOST = nextHost;
	return true;
}

function applyBackApiHostKeyFromConfig(instance: FrontendVariables, cfg: Record<string, string>): boolean {
	const backHostCfg = cfg["NEXT_PUBLIC_BACK_API_HOST"];
	if (!backHostCfg || instance.BACK_API_HOST === backHostCfg) {
		return false;
	}
	instance.BACK_API_HOST = backHostCfg;
	return true;
}

function applyBackApiKeysFromConfig(instance: FrontendVariables, cfg: Record<string, string>): boolean {
	let hasUpdate = false;
	if (cfg["NEXT_PUBLIC_BACK_API_PROTOCOL"] && instance.BACK_API_PROTOCOL !== cfg["NEXT_PUBLIC_BACK_API_PROTOCOL"]) {
		instance.BACK_API_PROTOCOL = cfg["NEXT_PUBLIC_BACK_API_PROTOCOL"];
		hasUpdate = true;
	}
	if (applyBackApiHostKeyFromConfig(instance, cfg)) {
		hasUpdate = true;
	}
	if (cfg["NEXT_PUBLIC_BACK_API_ROOT_URL"] && instance.BACK_API_ROOT_URL !== cfg["NEXT_PUBLIC_BACK_API_ROOT_URL"]) {
		instance.BACK_API_ROOT_URL = cfg["NEXT_PUBLIC_BACK_API_ROOT_URL"];
		hasUpdate = true;
	}
	if (cfg["NEXT_PUBLIC_BACK_API_VERSION"] && instance.BACK_API_VERSION !== cfg["NEXT_PUBLIC_BACK_API_VERSION"]) {
		instance.BACK_API_VERSION = cfg["NEXT_PUBLIC_BACK_API_VERSION"];
		hasUpdate = true;
	}
	if (applyLegacyBackApiHostRewriteInBrowser(instance)) {
		hasUpdate = true;
	}
	return hasUpdate;
}

function applyOptionalPublicConfigFields(instance: FrontendVariables, cfg: Record<string, string>): void {
	const frontHostCfg = cfg["NEXT_PUBLIC_FRONT_APP_HOST"] || cfg["FRONT_APP_HOST"];
	if (frontHostCfg) {
		instance.FRONT_APP_HOST = rewriteLegacyLecoffreIoFrontUrlOnV2NotaryPage(frontHostCfg);
		if (typeof window !== "undefined") {
			instance.FRONT_APP_HOST = alignMultisite4nkWebLineHostToPageLine(window.location.host, instance.FRONT_APP_HOST);
		}
	}
	if (cfg["NEXT_PUBLIC_IDNOT_BASE_URL"]) instance.IDNOT_BASE_URL = cfg["NEXT_PUBLIC_IDNOT_BASE_URL"];
	if (cfg["NEXT_PUBLIC_IDNOT_AUTHORIZE_ENDPOINT"]) instance.IDNOT_AUTHORIZE_ENDPOINT = cfg["NEXT_PUBLIC_IDNOT_AUTHORIZE_ENDPOINT"];
	if (cfg["NEXT_PUBLIC_IDNOT_CLIENT_ID"]) instance.IDNOT_CLIENT_ID = cfg["NEXT_PUBLIC_IDNOT_CLIENT_ID"];
	if (cfg["NEXT_PUBLIC_SPLASH_ID"]) instance.SPLASH_ID = cfg["NEXT_PUBLIC_SPLASH_ID"];
	if (cfg["NEXT_PUBLIC_SPLASH_MESSAGE"]) instance.SPLASH_MESSAGE = cfg["NEXT_PUBLIC_SPLASH_MESSAGE"];
	if (cfg["NEXT_PUBLIC_SPLASH_MAX_DISPLAYS"]) instance.SPLASH_MAX_DISPLAYS = cfg["NEXT_PUBLIC_SPLASH_MAX_DISPLAYS"];
}

export function applyPublicConfigPayloadToFrontendVariables(instance: FrontendVariables, cfg: Record<string, string>): boolean {
	const hasBackApiUpdate = applyBackApiKeysFromConfig(instance, cfg);
	applyOptionalPublicConfigFields(instance, cfg);
	return hasBackApiUpdate;
}

export async function invalidateBaseApiUrlAfterConfigUpdate(hasUpdate: boolean): Promise<void> {
	if (!hasUpdate) {
		return;
	}
	const { default: BaseApiService } = await import("@Front/Api/BaseApiService");
	BaseApiService.invalidateBaseUrl();
}
